import React, {useCallback, useEffect, useState} from 'react';
import {
    validateCardData,
    validateCheckData, validateContactInfoAndZeroCheck,
} from "./paymentFunctions";
import { useRequestUrl, useCaptchaKey } from "../context/contextHooks";
import { Turnstile } from '@marsidev/react-turnstile';
import { useStyles } from '../styles/styles';
import API from '../http/API';
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from '@mui/material/useMediaQuery'
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Box from "@mui/material/Box";
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Button from "@mui/material/Button";
import ReplayIcon from "@mui/icons-material/Replay";
import SendTwoToneIcon from '@mui/icons-material/SendTwoTone';
import CardData from './CardData';
import CheckData from './CheckData';
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import {getDecimal} from "../global/utils";
import Popup from '../layout/Popup';
import {HtmlComponent} from "../global/components";
import {getFeeQuery} from "./paymentFunctions";
import Verify from "./Verify";

const CompletePayment = (props) => {
    const openDialog = props.openDialog;
    const setOpenDialog = props.setOpenDialog;
    const paymentRequest = props.paymentRequest;
    const setPaymentRequest = props.setPaymentRequest;
    const setPaymentResult = props.setPaymentResult
    const court = props.court;
    const [requestUrl,] = useRequestUrl();
    const [captchaKey,] = useCaptchaKey();
    const theme = useTheme();
    const small = useMediaQuery(theme.breakpoints.down('md'));
    const tiny = useMediaQuery(theme.breakpoints.down('sm'));
    const classes = useStyles;
    const [tabVal, setTabVal] = useState('1');
    const [loading, setLoading] = useState(false);
    const [actionResponse, setActionResponse] = useState(null);
    const [paymentFields, setPaymentFields] = useState({paymentType: 'CARD', email: paymentRequest?.email, phone: paymentRequest?.phone, billingZip: paymentRequest?.billingZip});
    const [errorMessage, setErrorMessage] = useState(null);
    const [declineMessage, setDeclineMessage] = useState(null);
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [token, setToken] = useState(null);
    const [openNotice, setOpenNotice] = useState(!!court.prePaymentNotice);
    const [prepaymentShown, setPrepaymentShown] = useState(false);
    const captchaRef = React.useRef();
    const [noSubmit, setNoSubmit] = useState(false);
    const [verifyCompleted, setVerifyCompleted] = useState(false);
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [verifyRequired, setVerifyRequired] = useState(null);

    const [splitFeeAmts, setSplitFeeAmts] = useState({cardFee: 0, checkFee: 0, totalAmt: 0})

    const getPrePaymentNoticeComponent = () => {
        const title = 'Pre Payment Notice';
        return (
            <HtmlComponent title={title} component={court.prePaymentNotice} />
        );
    }

    const calculateFee = (paymentType) => {
        setErrorMessage(null);
        setLoading(true);
        const courtAmt = paymentRequest.courtAmount;
        const url = `${requestUrl}/calculateFee`;
        API.postAction(url, getFeeQuery(court, courtAmt, paymentType), 'calculateFee', null, setActionResponse);
    }

    const checkCardType = (cardNo, expMo, expYr) => {
        setErrorMessage(null);
        setLoading(true);
        const request = {courtId: court.courtId, cardNumber: cardNo, expMonth: expMo, expYear: expYr};
        const url = `${requestUrl}/checkCard`;
        API.postAction(url, request, 'checkCard', null, setActionResponse);
    }

    const getVerifyRequired = () => {
        setLoading(true);
        const url = `${requestUrl}/getVerifyRequired/${court.courtId}`;
        API.getRetrieve(url,'getVerifyRequired', null, setActionResponse);
    }

    const handleFeeResponse = (response) => {
        const fee = response.citepayFee;
        const total = response.totalAmount;
        setPaymentRequest({...paymentRequest, feeAmount: fee, totalAmount: total});
        if (court.onlineCheckFeeConfig && (court.checkFeeForDebit || !court.noChex) && tabVal === '1') {
            setSplitFeeAmts({cardFee: response.onlineCardFee, checkFee: response.onlineCheckFee, totalAmt: total});
        }
    }

    const handleCardResponse = (response) => {
        let feeAmt;
        if (splitFeeAmts) {
            if (court.checkFeeForDebit) {
                if ((response.checkCard || response.debitCard) && splitFeeAmts.checkFee) {
                    feeAmt = splitFeeAmts.checkFee;
                } else if (splitFeeAmts.cardFee) {
                    feeAmt = splitFeeAmts.cardFee;
                }
            } else feeAmt = splitFeeAmts.cardFee;
            if (feeAmt) {
                const totalAmt = parseFloat(getDecimal(paymentRequest.courtAmount + feeAmt, false));
                setPaymentRequest({...paymentRequest, feeAmount: feeAmt, totalAmount: totalAmt});
            }
        }
    }

    const handleVerificationReturned = (verifyFields) => {
        const request = {...paymentRequest, ...paymentFields, ...verifyFields};
        setVerifyOpen(false);
        sendPaymentRequest(request);
        setVerifyCompleted(false);
        setPaymentRequest(request);
    }

    const sendPaymentRequest = (request) => {
        setLoading(true);
        const url = `${requestUrl}/authorizePayment`;
        const payload = JSON.stringify(request);
        API.postAction(url, payload, 'authorizePayment', '', setActionResponse);
    }

    const handleTabChange = (e, newValue) => {
        setPaymentFields({...paymentFields, paymentType: (newValue === '2' ? 'CHECK' : 'CARD')});
        calculateFee(newValue === '2' ? 'CHECK' : 'CARD');
        setTabVal(newValue);
    }

    const Widget = useCallback( () => {
        if (!captchaKey) return;
        return (
            <Turnstile
                ref={captchaRef}
                siteKey={captchaKey}
                options={{
                    theme: 'light',
                    size: `${small ? 'compact' : 'normal'}`
                }}
                onSuccess={(token) => setToken(token)}
                onExpire={() => setToken(null)}
            />
        );
    }, [captchaKey, small]);

    const handleSubmit = () => {
        if (noSubmit) return;
        setNoSubmit(true);
        let sendRequest = false;
        setErrorMessage(null);
        let errors = paymentFields.paymentType === 'CHECK' ? validateCheckData(paymentFields, court) : validateCardData(paymentFields, court);
        if (errors) {
            setErrorMessage(errors);
        } else if (!token) {
            setErrorMessage("Please complete the Captcha box");
        } else {
            paymentRequest.totalAmount = parseFloat(getDecimal(paymentRequest.totalAmount, false));
            paymentRequest.paymentOperator = 'CitePay user';
            paymentRequest.captchaToken = token;
            const request = {...paymentRequest, ...paymentFields};
            errors = validateContactInfoAndZeroCheck(request, court);
            if (errors) {
                setErrorMessage(errors);
            } else if (verifyRequired && !verifyCompleted) {
                setVerifyOpen(true);
            } else {
                sendRequest = true;
                sendPaymentRequest(request);
                setToken(null);
            }
        }
        if (!sendRequest) setNoSubmit(false);
    }

    useEffect(() => {
        if (loading && actionResponse) {
            if (actionResponse.error) {
                setErrorMessage(actionResponse.errorMessage);
                captchaRef.current?.reset();
            } else if (actionResponse.requestType === 'calculateFee') {
                handleFeeResponse(actionResponse);
            } else if (actionResponse.requestType === 'checkCard') {
                handleCardResponse(actionResponse);
            } else if (actionResponse.requestType === 'getVerifyRequired') {
                setVerifyRequired(actionResponse.success);
            } else if (!actionResponse.authorized) {
                setDeclineMessage(actionResponse.declineMessage);
                captchaRef.current?.reset();
            } else {
                setPaymentResult(actionResponse);
                setOpenDialog(false);
            }
            setLoading(false);
            setNoSubmit(false);
        }
    }, [actionResponse])

    useEffect(() => {
        if (court.prePaymentNotice && prepaymentShown) {
            setOpenNotice(false);
        }
    }, [prepaymentShown])

    useEffect(() => {
        setAcceptTerms(false);
        setVerifyCompleted(false);
        setTabVal('1');
        if (openDialog && !prepaymentShown) setOpenNotice(!!court.prePaymentNotice);
        if (openDialog) getVerifyRequired();
    }, [openDialog])

    useEffect(() => {
        if (court && court.onlineCheckFeeConfig && (!court.noChex || court.checkFeeForDebit) && tabVal === '1' && !splitFeeAmts.cardFee > 0) {
            calculateFee('CARD');
        }
    }, [court, tabVal, splitFeeAmts]);

    return (
        <>
            { openNotice &&
                <Popup
                    open={openNotice}
                    setDone={setPrepaymentShown}
                    title="Payment Notice"
                    component={getPrePaymentNoticeComponent()}
                />
            }
            { captchaKey && !openNotice &&
                <Dialog
                    open={openDialog}
                    maxWidth='sm'
                    fullWidth
                    sx={classes.dialog}
                    PaperProps={{sx: classes.listContainer}}
                >
                    <Backdrop sx={classes.backdrop} open={loading}>
                        <CircularProgress color="inherit"/>
                    </Backdrop>
                    <DialogTitle sx={{ ...classes.title, fontWeight: 700 }} component="h2">Enter Payment Information</DialogTitle>
                    { errorMessage &&
                        <Alert severity='error' sx={{marginTop: '1rem', textAlign: 'left'}}>
                            <AlertTitle>Error!</AlertTitle>
                            {errorMessage}
                        </Alert>
                    }
                    { declineMessage &&
                        <Alert severity='warning' sx={{marginTop: '1rem', textAlign: 'left'}}>
                            {declineMessage}
                        </Alert>
                    }
                    { verifyOpen &&
                        <Verify
                            open={verifyOpen}
                            setOpen={setVerifyOpen}
                            paymentRequest={paymentRequest}
                            paymentFields={paymentFields}
                            court={court}
                            handleVerificationReturned={handleVerificationReturned}
                        />
                    }
                    <DialogContent dividers sx={{...classes.listContainer, minHeight: '60vh', padding: small ? '0' : ''}} >
                        <Box sx={{ width: '100%', typography: 'body1'}}>
                            {court.noChex ?
                                <CardData
                                    showTitle={true}
                                    paymentRequest={paymentRequest}
                                    paymentFields={paymentFields}
                                    setPaymentFields={setPaymentFields}
                                    setAcceptTerms={setAcceptTerms}
                                    checkCardType={checkCardType}
                                    splitFees={!!(court.onlineCheckFeeConfig && court.checkFeeForDebit)}
                                />:
                                <TabContext value={tabVal}>
                                    <Box sx={{...classes.title, borderBottom: 1, borderColor: 'divider', marginBottom: '0', paddingBottom: '0' }}>
                                        <TabList onChange={handleTabChange}>
                                            <Tab label='Card Payment' value='1' />
                                            <Tab label='Check Payment' value='2'/>
                                        </TabList>
                                    </Box>
                                    <TabPanel value='1'>
                                        <CardData
                                            showTitle={false}
                                            paymentRequest={paymentRequest}
                                            paymentFields={paymentFields}
                                            setPaymentFields={setPaymentFields}
                                            setAcceptTerms={setAcceptTerms}
                                            checkCardType={checkCardType}
                                            splitFees={court.onlineCheckFeeConfig}
                                        />
                                    </TabPanel>
                                    <TabPanel value='2'>
                                        <CheckData
                                            paymentRequest={paymentRequest}
                                            paymentFields={paymentFields}
                                            setPaymentFields={setPaymentFields}
                                            setAcceptTerms={setAcceptTerms}
                                            splitFees={court.onlineCheckFeeConfig}
                                        />
                                    </TabPanel>
                                </TabContext>
                            }
                        </Box>
                        <div style={{margin: '.5rem 0', display: 'flex', justifyContent: 'center'}}>
                            <div>
                                <Widget />
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions sx={{paddingTop: '0', justifyContent: small ? 'center' : 'flex'}}>
                        <Box component='div' sx={{display: 'flex', flexDirection: 'row-reverse', margin: '1rem', padding: '0'}}>
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={tiny ? null : <SendTwoToneIcon />}
                                disabled={!acceptTerms || !token || noSubmit}
                                sx={classes.squashedButton}
                                onClick={(event) => {
                                    event.target.disabled = true;
                                    handleSubmit();
                                    event.target.disabled = (!acceptTerms || !token || noSubmit)
                                }}
                            >
                                Submit
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={tiny ? null : <ReplayIcon/>}
                                sx={classes.squashedButton}
                                onClick={() => {
                                    setErrorMessage(null);
                                    setDeclineMessage(null);
                                    setPrepaymentShown(false);
                                    setOpenDialog(false);
                                }}
                            >
                                Cancel
                            </Button>
                        </Box>
                    </DialogActions>
                </Dialog>
            }
        </>
    );
}

export default CompletePayment;
